import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Plotly from "plotly.js-dist-min";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./MethanolChart.css";
import { buildNaAnnotation } from "./plotlyHelpers";
// ── Constants ─────────────────────────────────────────────────
const CHART_HEIGHT = 245;
const BAR_SLOT_WIDTH = 34;
const FIXED_BAR_PX = 17; // bar pixel width as per Figma (17px)
const PLOT_MARGIN_H = 75; // left (65) + right (10) Plotly margins
const X_AXIS_TRAILING_SLOTS = 1;
const MIN_CONTAINER_WIDTH = 420;
const CHART_MARGIN_OFFSET = 70;
const MAX_BAR_WIDTH_FRACTION = 0.9;
const CHART_MARGIN = { b: 40, l: 65, r: 10, t: 5 };
const Y_AXIS_RANGE_PADDING = 0.5;
const AXIS_FONT_SIZE = 11;
const AXIS_FONT_FAMILY = "Noto Sans, sans-serif";
const AXIS_TICK_COLOR = "#808080";
const GRID_COLOR = "#CCCCCC";
const GRID_LINE_WIDTH = 1;
const ZEROLINE_COLOR = "#000000";
const CHART_BG = "rgba(0,0,0,0)";
// ── Helpers ───────────────────────────────────────────────────
const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value);
// ── Component ─────────────────────────────────────────────────
const MethanolStackedBarChart = ({ data: chartData, series, testId, tooltipUnit, unit, xAxisLabel, yAxisMax, yAxisStepSize }) => {
    const containerRef = useRef(null);
    const plotRef = useRef(null);
    const [viewportWidth, setViewportWidth] = useState(700);
    const [customTooltip, setCustomTooltip] = useState(null);
    const plotInitialized = useRef(false);
    // Stable refs so hover handler always sees latest data without re-binding
    const chartDataRef = useRef(chartData);
    const seriesRef = useRef(series);
    chartDataRef.current = chartData;
    seriesRef.current = series;
    const resolvedTooltipUnit = tooltipUnit ?? unit;
    useEffect(() => {
        if (!containerRef.current)
            return;
        const setWidth = () => {
            if (!containerRef.current)
                return;
            setViewportWidth(Math.max(containerRef.current.clientWidth, MIN_CONTAINER_WIDTH));
        };
        setWidth();
        const resizeObserver = new ResizeObserver(setWidth);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);
    const chartWidth = Math.max(viewportWidth, (chartData.length + X_AXIS_TRAILING_SLOTS) * BAR_SLOT_WIDTH + CHART_MARGIN_OFFSET);
    // Compute bar width as a fraction of the category slot so it always renders
    // at the same pixel width as the original Chart.js FIXED_BAR_WIDTH (17px).
    const totalSlots = chartData.length + X_AXIS_TRAILING_SLOTS;
    const pixelsPerSlot = (chartWidth - PLOT_MARGIN_H) / totalSlots;
    const barWidthFraction = Math.min(FIXED_BAR_PX / pixelsPerSlot, MAX_BAR_WIDTH_FRACTION);
    // ── Build per-day sorted floating-bar traces ───────────────
    // Each day's segments are sorted ascending by value so the
    // status with the MOST hours ends up on TOP of the bar.
    const traces = useMemo(() => {
        const numDays = chartData.length;
        const xLabels = [...chartData.map((d) => d.date), ""];
        const slots = numDays + X_AXIS_TRAILING_SLOTS;
        // Per-trace arrays: one entry per day slot (trailing slot = 0)
        const traceValues = series.map(() => new Array(slots).fill(0));
        const traceBases = series.map(() => new Array(slots).fill(0));
        for (let dayIdx = 0; dayIdx < numDays; dayIdx++) {
            const datum = chartData[dayIdx];
            const presentIndices = datum.presentIndices;
            // Sort present statuses ascending → smallest at bottom, largest on top
            const presentItems = series
                .map((_, i) => ({ idx: i, value: datum.values[i] }))
                .filter((item) => presentIndices === undefined || presentIndices.includes(item.idx))
                .sort((a, b) => a.value - b.value);
            let cumulativeBase = 0;
            for (const item of presentItems) {
                traceBases[item.idx][dayIdx] = cumulativeBase;
                traceValues[item.idx][dayIdx] = item.value;
                cumulativeBase += item.value;
            }
        }
        return series.map((s, i) => ({
            base: traceBases[i],
            hoverinfo: "none",
            marker: { color: s.color, line: { width: 0 } },
            name: s.label,
            showlegend: false,
            type: "bar",
            width: barWidthFraction,
            x: xLabels,
            y: traceValues[i]
        }));
    }, [chartData, series, barWidthFraction]);
    // N/A annotations for days where backend sent no data at all
    const naAnnotations = useMemo(() => chartData.filter((d) => d.isNA).map((d) => buildNaAnnotation(d.date)), [chartData]);
    // ── Plotly layout ──────────────────────────────────────────
    const layout = useMemo(() => ({
        annotations: naAnnotations,
        barmode: "overlay",
        height: CHART_HEIGHT,
        hovermode: "x",
        margin: CHART_MARGIN,
        paper_bgcolor: CHART_BG,
        plot_bgcolor: CHART_BG,
        showlegend: false,
        width: chartWidth,
        xaxis: {
            fixedrange: true,
            showgrid: false,
            tickfont: { color: AXIS_TICK_COLOR, family: AXIS_FONT_FAMILY, size: AXIS_FONT_SIZE },
            type: "category",
            zeroline: false
        },
        yaxis: {
            autorange: false,
            dtick: yAxisStepSize,
            fixedrange: true,
            gridcolor: GRID_COLOR,
            gridwidth: GRID_LINE_WIDTH,
            range: [0, yAxisMax + yAxisStepSize * Y_AXIS_RANGE_PADDING],
            tickfont: { color: AXIS_TICK_COLOR, family: AXIS_FONT_FAMILY, size: AXIS_FONT_SIZE },
            tickformat: ",",
            ticksuffix: ` ${unit}`,
            zeroline: true,
            zerolinecolor: ZEROLINE_COLOR,
            zerolinewidth: GRID_LINE_WIDTH
        }
    }), [chartWidth, naAnnotations, unit, yAxisMax, yAxisStepSize]);
    const plotlyConfig = useMemo(() => ({ displayModeBar: false, responsive: false }), []);
    // ── Hover handlers (use refs so they stay stable) ──────────
    const handleHover = useCallback((eventData) => {
        const pt = eventData.points?.[0];
        if (!pt) {
            setCustomTooltip(null);
            return;
        }
        const xDate = pt.x;
        const datum = chartDataRef.current.find((d) => d.date === xDate);
        if (!datum || datum.isNA) {
            setCustomTooltip(null);
            return;
        }
        const containerBounds = containerRef.current?.getBoundingClientRect();
        if (!containerBounds) {
            setCustomTooltip(null);
            return;
        }
        const mouseEvent = eventData.event;
        const presentIndices = datum.presentIndices;
        const rows = seriesRef.current
            .map((s, i) => ({ _idx: i, color: s.color, label: s.label, value: datum.values[i] }))
            .filter((r) => presentIndices === undefined || presentIndices.includes(r._idx))
            .sort((a, b) => b.value - a.value)
            .map(({ color, label, value }) => ({ color, label, value }));
        setCustomTooltip({
            left: mouseEvent.clientX - containerBounds.left,
            rows,
            top: mouseEvent.clientY - containerBounds.top
        });
    }, []);
    const handleUnhover = useCallback(() => setCustomTooltip(null), []);
    // ── Initial plot (mount only) ──────────────────────────────
    useEffect(() => {
        if (!plotRef.current)
            return;
        const el = plotRef.current;
        Plotly.newPlot(el, traces, layout, plotlyConfig).then(() => {
            el.on("plotly_hover", handleHover);
            el.on("plotly_unhover", handleUnhover);
            plotInitialized.current = true;
        });
        return () => {
            Plotly.purge(el);
            plotInitialized.current = false;
        };
    }, []);
    // ── Update plot when data / size changes ───────────────────
    useEffect(() => {
        if (!plotRef.current || !plotInitialized.current)
            return;
        Plotly.react(plotRef.current, traces, layout, plotlyConfig);
    }, [traces, layout, plotlyConfig]);
    return (_jsxs("div", { className: "w-full", "data-test": testId, children: [_jsxs("div", { className: "mx-auto mb-1 flex h-[28px] items-center justify-center gap-3", "data-test": `${testId}-legend`, children: [_jsx("span", { className: "mr-1 inline-flex h-[16px] shrink-0 items-end align-bottom font-['Noto_Sans',sans-serif] text-[12px] font-normal leading-4 text-[#666565]", children: "Legend:" }), series.map((s) => (_jsxs("span", { className: "inline-flex h-[28px] items-center gap-2 p-1", "data-test": `${testId}-legend-item-${s.label.toLowerCase().replace(/[\s/]+/g, "-")}`, children: [_jsx("span", { className: "inline-block h-[8px] w-[8px] shrink-0 rounded-full", style: { backgroundColor: s.color } }), _jsx("span", { className: "whitespace-nowrap font-['Noto_Sans',sans-serif] text-[14px] font-semibold leading-5 text-[#3F3F3F]", children: s.label })] }, s.label)))] }), _jsxs("div", { className: "relative w-full", "data-test": `${testId}-resize-container`, ref: containerRef, children: [customTooltip && (_jsxs("div", { className: "pointer-events-none absolute z-50 whitespace-nowrap rounded-md border border-[#e0e0e0] bg-white px-3 py-[7px] text-xs shadow-[0_4px_12px_rgb(0_0_0/12%)] [transform:translate(-50%,calc(-100%-12px))]", "data-test": `${testId}-tooltip`, style: { left: customTooltip.left, top: customTooltip.top }, children: [customTooltip.rows.map((row) => (_jsxs("div", { className: "flex items-center gap-2 py-0.5", "data-test": `${testId}-tooltip-row-${row.label.toLowerCase().replace(/[\s/]+/g, "-")}`, children: [_jsx("span", { className: "inline-block h-[8px] w-[8px] shrink-0 rounded-full", style: { backgroundColor: row.color } }), _jsx("span", { className: "flex-1 text-left font-normal text-gray-500", children: row.label }), _jsxs("span", { className: "ml-4 font-bold text-gray-900", "data-test": `${testId}-tooltip-row-value-${row.label.toLowerCase().replace(/[\s/]+/g, "-")}`, children: [formatNumber(row.value), " ", resolvedTooltipUnit] })] }, row.label))), _jsx("span", { className: "absolute bottom-[-8px] left-1/2 h-0 w-0 -translate-x-1/2 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[8px] border-t-[#e0e0e0] after:absolute after:bottom-[2px] after:left-1/2 after:h-0 after:w-0 after:-translate-x-1/2 after:border-l-[6px] after:border-l-transparent after:border-r-[6px] after:border-r-transparent after:border-t-[7px] after:border-t-white after:content-['']" })] })), _jsx("div", { className: "methanol-chart-scroll", "data-test": `${testId}-scroll-container`, children: _jsx("div", { "data-test": `${testId}-chart-wrapper`, ref: plotRef, style: { height: `${CHART_HEIGHT}px`, width: `${chartWidth}px` } }) }), _jsx("div", { className: "pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 text-center font-['Noto_Sans',sans-serif] text-[14px] font-normal leading-5 text-[#666565]", "data-test": `${testId}-x-axis-label`, children: xAxisLabel })] })] }));
};
export default MethanolStackedBarChart;
